import MaterialTable, { MTableBodyRow } from "@material-table/core";
import {
  TextField,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Paper,
  Tooltip,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Close, Done, Refresh } from "@material-ui/icons";
import React, { createRef, useEffect, useState } from "react";
import DateRangeField from "../components/DateRangeField/DateRangeField";
import FilterAction from "../components/FilterAction/FilterAction";
import { usePopups } from "../contexts/PopupContext";
import { useGlobalSnackbar } from "../contexts/SnackbarContext";
import { useUser } from "../contexts/UserContextProvider";
import useApi from "../hooks/useApi";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";

import {
  dateFormatterComplete,
  DEMO_PATIENT_DEV,
  DEMO_PATIENT_PROD,
  getSessionEnd,
  getSessionStart,
  onlyDateFormatter,
} from "../utils/utils";
import LinkButton from "../components/LinkButton/LinkButton";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  content: {
    fontSize: "3ex",
  },
  card: {
    padding: "2%",
  },
  title: {
    fontWeight: "lighter",
    paddingBottom: "1%",
  },
  paper: {
    padding: "1rem",
    marginBlockEnd: "1rem",
    display: "flex",
    alignItems: "center",
    gap: ".5rem",
  },
  container: {
    gap: "1rem",
    display: "flex",
    flexWrap: "wrap",
    background: "white",
    padding: "1rem",
    borderTopLeftRadius: "1rem",
    borderTopRightRadius: "1rem",
    maxWidth: "max-content",
  },
  deleted: {
    "&:hover": {
      position: "relative",
      "& td:last-child::before": {
        content: "'cancellata'",
        position: "absolute",
        inset: 0,
        display: "grid",
        placeItems: "center",
        fontSize: "1.5rem",
        textTransform: "uppercase",
        color: "red",
        backgroundColor: "rgb(255 255 255 / .8)",
      },
    },
  },
});

export default function Session() {
  const { t } = useTranslation(["views", "common"]);
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showExternal, setShowExternal] = useState(true);
  const [sessions, setSessions] = useState([]);

  const [user] = useUser();

  const refresh = () => {
    if (user?.profile?.clinicCode) {
      getSessions();
    }
  };

  const handleToggleFilter = (setter, value) => {
    setter(value);
    refresh();
  };

  const tableRef = createRef();

  const [range, setRange] = useState([]);
  const startDate = range?.[0]?.toISOString?.() ?? "";
  const endDate = range?.[1]?.toISOString?.() ?? "";
  const api = useApi();

  const [, open] = usePopups();

  const setGlobalSnackbar = useGlobalSnackbar();

  async function getSessions() {
    setLoading(true);
    const endpoint = `sessions/clinicalcode/${user?.profile?.clinicCode}/all?startDate=${startDate}&endDate=${endDate}`;
    const response = await api(endpoint, "GET", null, () => {
      open("sessionExpiredPopup");
    });
    setLoading(false);
    if (response.ok !== false) {
      const filtereSessions = response.sessions.filter(
        (session) =>
          ![DEMO_PATIENT_DEV, DEMO_PATIENT_PROD].includes(session.idPatient),
      );
      setSessions(filtereSessions);
      return;
    }
    setGlobalSnackbar({
      open: true,
      severity: "error",
      message:
        `${t("common:errors.sessions")}`,
    });
  }

  useEffect(() => {
    if (user?.profile?.clinicCode) getSessions();
  }, [user?.profile?.clinicCode, range]);

  return (
    <div className={classes.card}>
      <Typography className={classes.title} variant="h4">
        {t("views:sessions.title")}
      </Typography>
      <Paper className={classes.paper}>
        {t("views:sessions.filter_date")}{" "}
        <DateRangeField
          range={range}
          onChange={(newRange) => {
            setRange(newRange);
          }}
        />
      </Paper>
      <div className={classes.content}>
        <MaterialTable
          isLoading={loading}
          components={{
            Row: ({ data, ...props }) => {
              return (
                <MTableBodyRow
                  className={data.isDeleted ? classes.deleted : undefined}
                  data={data}
                  {...props}
                />
              );
            },
          }}
          columns={[
            {
              title: `${t("common:label.therapist")}`,
              field: "therapistFullName",
              customSort: (a, b) => {
                const nameA =
                  `${a.therapistLastname} ${a.therapistFirstName}`.toLowerCase();
                const nameB =
                  `${b.therapistLastname} ${b.therapistFirstName}`.toLowerCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
              },
              customFilterAndSearch: (term, rowData) =>
                `${rowData.therapistLastname} ${rowData.therapistFirstName}`
                  .toLowerCase()
                  .includes(term.toLowerCase()),
              render: (rowData) => (
                <Tooltip title={t("common:tooltip.therapist_profile")}>
                  <span>
                    <LinkButton
                      onClick={() => {
                        history.push(`/therapists/${rowData.idTherapist}`);
                      }}
                    >{`${rowData.therapistLastname ?? ""} ${
                      rowData.therapistFirstName ?? ""
                    }`}</LinkButton>
                  </span>
                </Tooltip>
              ),
            },
            {
              title: `${t("common:label.start_session")}`,
              field: "sessionStart",
              type: "datetime",
              filtering: false,
              searchable: false,
              defaultSort: "desc",
              render: (row) => {
                const sessionStart = getSessionStart(row);
                const sessionEnd = getSessionEnd(row);
                return sessionStart
                  ? dateFormatterComplete.format(sessionStart)
                  : sessionEnd
                    ? `${t("common:label.cancelled_session")}`
                    : `${t("common:label.not_started_session")}`;
              },
              customFilterAndSearch: (term, row) => {
                const sessionStart = getSessionStart(row);
                const sessionEnd = getSessionEnd(row);
                return (
                  sessionStart
                    ? onlyDateFormatter.format(sessionStart)
                    : sessionEnd
                      ? `${t("common:label.cancelled_session")}`
                      : `${t("common:label.not_started_session")}`
                )
                  .toLowerCase()
                  .includes(term.toLowerCase());
              },
            },
            {
              title: `${t("common:label.end_session")}`,
              field: "sessionEnd",
              type: "datetime",
              searchable: false,
              filtering: false,
              render: (row) => {
                const sessionStart = getSessionStart(row);
                const sessionEnd = getSessionEnd(row);
                return sessionStart
                  ? sessionEnd
                    ? dateFormatterComplete.format(sessionEnd)
                    : `${t("common:label.session_progress")}`
                  : sessionEnd
                    ? `${t("common:label.cancelled_session")}`
                    : `${t("common:label.not_started_session")}`;
              },
              customFilterAndSearch: (term, row) => {
                const sessionStart = getSessionStart(row);
                const sessionEnd = getSessionEnd(row);
                return (
                  sessionStart
                    ? onlyDateFormatter.format(sessionStart)
                    : sessionEnd
                      ? `${t("common:label.cancelled_session")}`
                      : `${t("common:label.not_yet_started_session")}`
                )
                  .toLowerCase()
                  .includes(term.toLowerCase());
              },
            },
            {
              title: `${t("common:label.patient")}`,
              // type: "string",
              field: "patientFullName",
              defaultSort: "asc",
              customSort: (a, b) => {
                const nameA =
                  `${a.patientLastName} ${a.patientFirstName}`.toLowerCase();
                const nameB =
                  `${b.patientLastName} ${b.patientFirstName}`.toLowerCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
              },
              customFilterAndSearch: (term, rowData) =>
                `${rowData.patientLastName} ${rowData.patientFirstName}`
                  .toLowerCase()
                  .includes(term.toLowerCase()),
              render: (rowData) => (
                <Tooltip title={t("common:tooltip.patient_profile")}>
                  <span>
                    <LinkButton
                      onClick={() => {
                        history.push(`patients/${rowData.idPatient}`);
                      }}
                    >{`${rowData.patientLastName ?? ""} ${
                      rowData.patientFirstName ?? ""
                    }`}</LinkButton>
                  </span>
                </Tooltip>
              ),
            },
            {
              title: "PsyCare",
              filtering: false,
              customSort: (a, b) => {
                const valA = a.isTimer || a.isExternal ? 1 : 0;
                const valB = b.isTimer || b.isExternal ? 1 : 0;
                return valA - valB;
              },
              render: (row) => {
                return row.isTimer || row.isExternal ? (
                  <Close style={{ color: "red" }} />
                ) : (
                  <Done style={{ color: theme.palette.primary.main }} />
                );
              },
              searchable: false,
            },
          ]}
          data={sessions}
          actions={[
            {
              icon: Refresh,
              onClick: () => {
                if (user?.profile?.clinicCode) getSessions();
              },
              isFreeAction: true,
            },
            {
              icon: () => (
                <FilterAction
                  open={isFilterPanelOpen}
                  onClose={() => {
                    setIsFilterPanelOpen(false);
                  }}
                >
                  <strong>{t("common:label.filters")}</strong>
                  <Divider />
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={showDeleted}
                        onChange={(e) =>
                          handleToggleFilter(setShowDeleted, e.target.checked)
                        }
                      />
                    }
                    label={t("views:sessions.cancelled_session")}
                  />
                  <br />
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={showExternal}
                        onChange={(e) =>
                          handleToggleFilter(setShowExternal, e.target.checked)
                        }
                      />
                    }
                    label={t("views:sessions.external_session")}
                  />
                </FilterAction>
              ),
              isFreeAction: true,
              onClick: () => {
                setIsFilterPanelOpen((prev) => !prev);
              },
            },
          ]}
          options={{
            draggable: false,
            debounceInterval: 250,
            filtering: false,
            showTitle: false,
            thirdSortClick: false,
            rowStyle: (rowData) => ({
              fontSize: "1rem",
              backgroundColor: rowData?.isDeleted ? "#dedede" : null,
              opacity: rowData?.isDeleted ? 0.5 : null,
            }),
          }}
          localization={{
            header: {
              actions: "",
            },
            body: {
              emptyDataSourceMessage: (
                <>
                  {t("views:sessions.empty_table_title")} <br />
                 {t("views:sessions.empty_table_subtitle")}
                </>
              ),
            },
            toolbar: {
              searchPlaceholder: `${t("common:table.search_placeholder")}`,
              searchTooltip: `${t("common:table.search_tooltip")}`,
            },
            pagination: {
              labelDisplayedRows: `{from}-{to} ${t("common:table.displayed_rows")} {count}`,
              labelRowsSelect: `${t("common:table.rows")}`,
              labelRowsPerPage: `${t("common:table.rows_page")}`,
              firstTooltip: `${t("common:table.first_tooltip")}`,
              lastTooltip: `${t("common:table.last_tooltip")}`,
              nextTooltip: `${t("common:table.next_tooltip")}`,
              previousTooltip: `${t("common:table.previous_tooltip")}`,
            },
          }}
        />
      </div>
    </div>
  );
}
